import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import SolutionModal from "@/components/SolutionModal";

export default function SolutionsGrid({ division, onEnquire }) {
  const [selected, setSelected] = useState(null);

  return (
    <section id="solutions" className="scroll-mt-24 px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24" data-testid="solutions-section">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-2xl sm:mb-14" data-reveal>
          <p className="kicker mb-4">Products & Solutions</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {division.label} solutions, engineered end to end
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
            BIGBOSS DIGITECH delivers end-to-end ELV and smart automation solutions for{" "}
            {division.label.toLowerCase()} properties — security, networking and audio-visual systems
            designed on AutoCAD, installed by certified engineers and serviced within 12–24 hours.
            Fifteen-plus solutions, trusted partner brands, one accountable vendor. Select any system
            for details.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
          {division.solutions.map((s, i) => (
            <button
              key={s.id}
              data-testid={`solution-card-${s.id}`}
              data-reveal
              data-delay={(i % 4) * 0.08}
              onClick={() => setSelected(s)}
              className="group relative flex min-h-[16rem] flex-col justify-end overflow-hidden rounded-2xl border border-slate-700/70 text-left transition-all duration-500 hover:-translate-y-1.5 hover:border-brand/70 hover:shadow-2xl hover:shadow-brand/10"
              aria-label={`View details of ${s.title}`}
            >
              <img
                src={s.img}
                alt={`${s.title} — ${division.label} solution`}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = division.fallbackImg;
                }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/35 to-transparent" />
              <div className="relative flex items-end justify-between gap-3 p-5 pt-20">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-white">{s.title}</h3>
                  {s.brands.length > 0 && (
                    <p className="mt-1 font-tech text-[10px] uppercase leading-relaxed tracking-widest text-slate-400">
                      {s.brands.join(" · ")}
                    </p>
                  )}
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-500/80 text-slate-300 transition-all duration-300 group-hover:rotate-90 group-hover:border-brand group-hover:bg-brand group-hover:text-white">
                  <Plus size={16} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <SolutionModal
            solution={selected}
            divisionLabel={division.label}
            onClose={() => setSelected(null)}
            onEnquire={(s) => {
              setSelected(null);
              onEnquire(s);
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
